import axios from 'axios';
import { API_BASE } from '../config';

const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Attach JWT token to every request
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  // Let the browser set multipart boundaries for FormData uploads.
  if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
    delete config.headers['Content-Type'];
  }

  return config;
});

// Prevents the session-expired event from firing more than once per expiry cycle.
let _sessionExpiredDispatched = false;

export const resetSessionExpiredFlag = () => {
  _sessionExpiredDispatched = false;
};

// Same latch for the tenant-license block: a page that fires ten parallel
// requests would otherwise dispatch ten identical events.
let _licenseBlockedDispatched = false;

export const resetLicenseBlockedFlag = () => {
  _licenseBlockedDispatched = false;
};

// Handle 401 responses globally — only evict session when the user is inside
// the app, not on the login/register pages where secondary background calls
// can transiently return 401 before the session is fully established.
// Instead of a hard page redirect (which would wipe unsaved form data), we
// dispatch an event so AuthContext can show a re-login modal overlay.
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const onAuthPage = ['/login', '/register'].some((p) =>
        window.location.pathname === p || window.location.pathname.startsWith(p + '/')
      );
      if (!onAuthPage && !_sessionExpiredDispatched) {
        _sessionExpiredDispatched = true;
        // Clear the expired token so subsequent requests don't loop on 401.
        localStorage.removeItem('token');
        // Signal the app to show a re-login modal without unmounting the page.
        window.dispatchEvent(new CustomEvent('journey:session-expired'));
      }
    }

    // 402 = the TENANT's subscription has lapsed or been suspended (server:
    // middleware/licenseGate.js). Distinct from 401 on purpose — the session is
    // perfectly valid, the licence is not.
    //
    // Gated on `license_expired` rather than the status code alone, so an
    // unrelated 402 from some future payment integration can never blank the app.
    if (error.response?.status === 402 && error.response?.data?.license_expired) {
      if (!_licenseBlockedDispatched) {
        _licenseBlockedDispatched = true;
        // Deliberately NOT clearing the token. Clearing it would drop the user
        // to /login, where the same 402 answers them again with no way forward,
        // and they would lose the session they will need the moment the owner
        // extends the licence.
        window.dispatchEvent(new CustomEvent('journey:license-expired', {
          detail: error.response.data,
        }));
      }
    }

    return Promise.reject(error);
  }
);

export default api;
